// src/app/pages/orders/AgreedShippingPanel.js

import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Card, Grid, IconButton, Stack, TextField, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingTwoToneIcon from '@mui/icons-material/LocalShippingTwoTone';
import AddressAutocomplete from 'app/widgets/Shared/AddressAutocomplete/AddressAutocomplete';
import { googlePlaceToAddress } from 'app/pages/customers/LocationDialog';
import { getDropoffCarrierByKey, getDropoffCarrierOptions } from 'app/utils/dropoffCarriers';
import { formatRut, hasValidRutFormat } from 'app/utils/rut';

export const AGREED_MODE_LABELS = {
    pickup: 'Retiro en bodega',
    own_fleet: 'Flota propia',
    courier: 'Courier',
};

// Opcion de la lista de carriers para dejarlo como retiro en bodega.
const PICKUP_OPTION = { value: 'pickup', label: 'Retiro en bodega' };

// Lo que el bridge de ML escribia en el comentario de un pedido sin envio.
const PICKUP_COMMENT = 'RETIRA EN BODEGA';

// Lo que dice el chip del listado una vez definido: el courier o la modalidad.
export const agreedShippingLabel = (agreed, carriers) => {
    if (!agreed) return 'Acordar con vendedor';
    const carrier = agreed.carrier && getDropoffCarrierOptions(carriers).find((c) => c.value === agreed.carrier);
    return carrier?.label || agreed.carrier || AGREED_MODE_LABELS[agreed.mode] || 'Acordado';
};

// Texto legible de una direccion guardada ({ street, street_number, city }).
const addressText = (addr) => {
    if (!addr) return '';
    const street = [addr.street, addr.street_number].filter(Boolean).join(' ');
    return [street, addr.city].filter(Boolean).join(', ') || addr.address || '';
};

// El comentario sin el "RETIRA EN BODEGA" que el bridge escribia antes en estos pedidos.
const userComment = (comment) => (
    String(comment || '').split('|').map((c) => c.trim()).filter((c) => c && c !== PICKUP_COMMENT).join(' | ')
);

// La direccion escrita se resuelve con el Geocoder de Google al guardar, y se manda
// desglosada con lat/lng: el backend no re-geocodifica (ver Location.geocode_for_geofence).
const geocodeAddress = (text) => new Promise((resolve) => {
    const Geocoder = window?.google?.maps?.Geocoder;
    if (!Geocoder) return resolve(null);

    new Geocoder().geocode(
        { address: text, componentRestrictions: { country: 'CL' } },
        (results, status) => resolve(status === 'OK' && results?.[0] ? googlePlaceToAddress(results[0]) : null)
    );
});

/**
 * Pedido de Mercado Libre "acordar con el vendedor": ML no crea envio ni manda la
 * direccion del comprador, y el pedido entra como retiro en bodega. En este panel se le
 * anida al pedido lo acordado con el comprador: carrier, direccion y comentario.
 *
 * La modalidad no se elige, sale de lo que se llena:
 *   "Retiro en bodega" en carrier -> retiro en bodega
 *   con carrier                   -> courier (va por dropoff al carrier)
 *   con direccion, sin carrier    -> flota propia
 *   sin direccion ni carrier      -> retiro en bodega
 */
const AgreedShippingPanel = ({ order, carriers, onClose, onSave, saving }) => {
    const [carrier, setCarrier] = useState(null);
    const [address, setAddress] = useState('');
    const [comment, setComment] = useState('');
    const [rut, setRut] = useState('');
    const [phone, setPhone] = useState('');
    const [error, setError] = useState(null);
    const [resolving, setResolving] = useState(false);

    const savedAddressText = addressText(order?.agreed_address);

    // Mercado Libre no es un carrier posible: el pedido justamente no tiene envio de ML.
    // El retiro en bodega va primero en la misma lista, para marcarlo sin buscar otro campo.
    const carrierOptions = useMemo(
        () => [PICKUP_OPTION, ...getDropoffCarrierOptions(carriers).filter((c) => c.value !== 'mercadolibre')],
        [carriers]
    );

    // Direccion por defecto del carrier, la que la compañia configura en Editar compañia.
    const defaultAddressOf = (c) => (c ? getDropoffCarrierByKey(c.value, carriers)?.defaultAddress?.text : '') || '';

    useEffect(() => {
        if (!order) return;
        const agreedCarrier = order.agreed_shipping?.mode === 'pickup' ? PICKUP_OPTION.value : order.agreed_shipping?.carrier;
        setCarrier(agreedCarrier ? carrierOptions.find((c) => c.value === agreedCarrier) || null : null);
        setAddress(savedAddressText);
        setComment(userComment(order.comment));
        setRut(order.agreed_contact?.rut || '');
        setPhone(order.agreed_contact?.phone || '');
        setError(null);
    }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Al elegir carrier la direccion parte con la suya por defecto, como en Enviar a preparacion.
    // Si ya se habia escrito otra a mano no se pisa.
    const changeCarrier = (next) => {
        if (next?.value !== PICKUP_OPTION.value && (!address.trim() || address === defaultAddressOf(carrier))) setAddress(defaultAddressOf(next) || savedAddressText);
        setCarrier(next);
    };

    const pickupChosen = carrier?.value === PICKUP_OPTION.value;
    const mode = pickupChosen ? 'pickup' : carrier ? 'courier' : (address.trim() ? 'own_fleet' : 'pickup');
    const rutInvalid = rut.trim() !== '' && !hasValidRutFormat(rut);

    const modeHint = {
        courier: `Sale por ${carrier?.label}: flota propia lo deja en el carrier.`,
        own_fleet: 'Sin carrier: lo lleva la flota propia a esta dirección.',
        pickup: pickupChosen ? 'El comprador lo pasa a buscar a la bodega.' : 'Sin carrier ni dirección: queda como retiro en bodega.',
    }[mode];

    const handleSave = async () => {
        setError(null);
        if (rutInvalid) return setError('El RUT no es válido.');
        if (mode === 'courier' && !address.trim()) return setError('Falta la dirección donde se entrega al carrier.');

        let resolved = null;
        if (mode !== 'pickup') {
            // Sin cambios en el texto se reusa la direccion ya guardada, con sus coordenadas.
            if (address === savedAddressText && order?.agreed_address?.lat) {
                resolved = order.agreed_address;
            } else {
                setResolving(true);
                resolved = await geocodeAddress(address);
                setResolving(false);
                if (!resolved?.lat) return setError('No encontramos esa dirección en el mapa. Elige una de las sugerencias.');
                if (!resolved.city) return setError('A la dirección le falta la comuna. Elige una sugerencia más precisa.');
            }
        }

        onSave({
            mode,
            address: resolved,
            carrier: mode === 'courier' ? carrier.value : null,
            rut: rut.trim() ? formatRut(rut) : null,
            phone: phone.trim() || null,
            comment: comment.trim(),
        });
    };

    if (!order) return null;

    return (
        <Card sx={{ mb: 2, p: 2 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                        <LocalShippingTwoToneIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        Acordar con vendedor
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Pedido {order.code}: Mercado Libre no trae envío ni dirección. Lo que acordaste con el comprador queda en el pedido.
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Stack>

            <Stack spacing={2} sx={{ mt: 2 }}>
                <Autocomplete
                    size="small"
                    options={carrierOptions}
                    value={carrier}
                    onChange={(e, v) => changeCarrier(v)}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    renderInput={(params) => <TextField {...params} label="Carrier" helperText="Déjalo vacío si lo lleva la flota propia." />}
                />

                {/* El mismo buscador de Enviar a preparacion: sugiere direcciones desde la 3a letra. */}
                {!pickupChosen && (
                    <AddressAutocomplete
                        label={carrier ? `Dirección de entrega a ${carrier.label}` : 'Dirección de entrega'}
                        placeholder="Ej: Av. Providencia 1234, Providencia"
                        value={address}
                        onChange={setAddress}
                        helperText={
                            address && address === defaultAddressOf(carrier) ? 'Dirección por defecto del carrier'
                                : address && address === savedAddressText ? 'Dirección guardada en el pedido'
                                    : 'Queda solo en este pedido: la del cliente no cambia.'
                        }
                    />
                )}

                {mode !== 'pickup' && (
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                size="small"
                                label="RUT del comprador"
                                value={rut}
                                onChange={(e) => setRut(e.target.value)}
                                onBlur={() => hasValidRutFormat(rut) && setRut(formatRut(rut))}
                                fullWidth
                                placeholder="12.345.678-9"
                                error={rutInvalid}
                                helperText={rutInvalid ? 'RUT inválido' : 'Lo pide la etiqueta'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                size="small"
                                label="Teléfono"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                fullWidth
                                helperText="Para avisarle al comprador"
                            />
                        </Grid>
                    </Grid>
                )}

                <TextField
                    size="small"
                    label="Comentario"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                    helperText="Se ve en el pedido y en la app del chofer."
                />

                <Alert severity="info" icon={false}>
                    <strong>{AGREED_MODE_LABELS[mode]}.</strong> {modeHint}
                </Alert>

                {error && <Alert severity="error">{error}</Alert>}

                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <Button size="small" onClick={onClose} disabled={saving || resolving}>Cancelar</Button>
                    <Button size="small" variant="contained" onClick={handleSave} disabled={saving || resolving}>
                        {resolving ? 'Buscando dirección…' : 'Guardar'}
                    </Button>
                </Stack>
            </Stack>
        </Card>
    );
};

export default AgreedShippingPanel;
